import React, { useEffect, useMemo, useRef, useState } from "react";
import { bookParser, HeadingItem } from "../services/latexBookParser";
import "./DraftPreview.css";

interface DraftPreviewProps {
  content: string;
  filePath: string;
  cursorLine?: number | null;
  onNavigateToLine?: (line: number) => void;
}

export const DraftPreview: React.FC<DraftPreviewProps> = ({
  content,
  filePath,
  cursorLine,
  onNavigateToLine,
}) => {
  const [showOutline, setShowOutline] = useState(false);
  const [fontSizeRatio, setFontSizeRatio] = useState(1);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const pageRef = useRef<HTMLDivElement | null>(null);

  // Compute parsed HTML and track rendering latency
  const { html, headings, renderTimeMs } = useMemo(() => {
    const t0 = performance.now();
    const res = bookParser.parse(content);
    const t1 = performance.now();
    return {
      html: res.html,
      headings: res.headings,
      renderTimeMs: Math.max(1, Math.round(t1 - t0)),
    };
  }, [content]);

  // Synchronize scroll & highlight when cursorLine in editor changes
  useEffect(() => {
    if (!cursorLine || !pageRef.current) return;
    const page = pageRef.current;

    // Clear previous sync highlights
    page.querySelectorAll(".draft-active-line-sync, .draft-sentence-sync").forEach((el) => {
      el.classList.remove("draft-active-line-sync", "draft-sentence-sync");
    });

    // 1. Try to find exact line match: [data-line="X"]
    let target = page.querySelector(`[data-line="${cursorLine}"]`) as HTMLElement | null;

    // 2. If not found, find enclosing block: [data-line-start <= X <= data-line-end]
    if (!target) {
      const blocks = page.querySelectorAll<HTMLElement>("[data-line-start]");
      for (const b of blocks) {
        const start = parseInt(b.getAttribute("data-line-start") || "0", 10);
        const end = parseInt(b.getAttribute("data-line-end") || "0", 10);
        if (start <= cursorLine && cursorLine <= end) {
          target = b;
          break;
        }
      }
    }

    // 3. Fallback: closest block
    if (!target) {
      let closest: HTMLElement | null = null;
      let closestDiff = Infinity;
      const blocks = page.querySelectorAll<HTMLElement>("[data-line-start]");
      for (const b of blocks) {
        const start = parseInt(b.getAttribute("data-line-start") || "0", 10);
        const diff = Math.abs(start - cursorLine);
        if (diff < closestDiff) {
          closestDiff = diff;
          closest = b;
        }
      }
      target = closest;
    }

    if (target) {
      if (target.classList.contains("book-line")) {
        target.classList.add("draft-sentence-sync");
        const parentPara = target.closest(".book-para");
        if (parentPara) parentPara.classList.add("draft-active-line-sync");
      } else {
        target.classList.add("draft-active-line-sync");
      }

      target.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [cursorLine, html]);

  // Handle clicking on any element in the web preview to jump editor back to that line
  const handlePageClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const target = (e.target as HTMLElement).closest("[data-line], [data-line-start]");
    if (target) {
      const lineAttr = target.getAttribute("data-line") || target.getAttribute("data-line-start");
      if (lineAttr) {
        const line = parseInt(lineAttr, 10);
        if (!isNaN(line) && line > 0) {
          onNavigateToLine?.(line);
        }
      }
    }
  };

  const handleHeadingClick = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      setShowOutline(false);
    }
  };

  const fileName = filePath ? filePath.split("/").pop() : "پیش‌نویس";

  return (
    <div className="draft-preview-container">
      <div className="draft-preview-toolbar">
        <div className="draft-toolbar-left">
          <span className="draft-badge-fast" title="زمان رندر بلادرنگ در مرورگر">
            ⚡ {renderTimeMs}ms
          </span>
          {cursorLine && (
            <span className="draft-sync-badge" title="خط متناظر انتخاب‌شده در ادیتور">
              🎯 خط {cursorLine}
            </span>
          )}
          <span className="draft-doc-title">{fileName}</span>
        </div>

        <div className="draft-toolbar-right">
          <button
            type="button"
            className="draft-tool-btn"
            onClick={() => setFontSizeRatio((r) => Math.max(0.8, Number((r - 0.1).toFixed(1))))}
            title="کاهش اندازه فونت"
          >
            A-
          </button>
          <button
            type="button"
            className="draft-tool-btn"
            onClick={() => setFontSizeRatio(1)}
            title="اندازه پیش‌فرض"
          >
            ۱۰۰٪
          </button>
          <button
            type="button"
            className="draft-tool-btn"
            onClick={() => setFontSizeRatio((r) => Math.min(1.5, Number((r + 0.1).toFixed(1))))}
            title="افزایش اندازه فونت"
          >
            A+
          </button>

          {headings.length > 0 && (
            <button
              type="button"
              className={`draft-tool-btn${showOutline ? " active" : ""}`}
              onClick={() => setShowOutline((s) => !s)}
              title="فهرست سرفصل‌های این بخش"
            >
              📑 سرفصل‌ها ({headings.length})
            </button>
          )}
        </div>
      </div>

      {showOutline && headings.length > 0 && (
        <div className="draft-outline-drawer">
          <div className="draft-outline-title">سرفصل‌های سند</div>
          {headings.map((h: HeadingItem) => (
            <button
              key={h.id}
              type="button"
              className={`draft-outline-item level-${h.level}`}
              onClick={() => handleHeadingClick(h.id)}
            >
              {h.title}
            </button>
          ))}
        </div>
      )}

      <div className="draft-preview-scroll" ref={scrollContainerRef}>
        <div
          ref={pageRef}
          className="draft-book-page"
          style={{ fontSize: `${14 * fontSizeRatio}px` }}
          dangerouslySetInnerHTML={{ __html: html }}
          onClick={handlePageClick}
          title="برای پرش به خط متناظر در ادیتور کلیک کنید"
        />
      </div>
    </div>
  );
};
